import React, { useState, useEffect, useRef } from 'react';
import { Container, Spinner, Tabs, Tab, Modal, Row, Col, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ResponsiveTable from '../Grid/ResponsiveTable';
import InputBox from '../common/InputBox';
import SearchSelect from '../common/SearchSelect';
import { getErrorLogs, getLoginLogs, getAuditTrail } from '../../services/auditTrail-service';

const AuditTrail = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('auditTrail');
  const [userRole, setUserRole] = useState('');

  const [errorLogs, setErrorLogs] = useState([]);
  const [errorDate, setErrorDate] = useState(getToday());
  const [showErrorDetail, setShowErrorDetail] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  const [loginLogs, setLoginLogs] = useState([]);
  const [loginDate, setLoginDate] = useState(getToday());
  const [showLoginDetail, setShowLoginDetail] = useState(false);
  const [selectedLogin, setSelectedLogin] = useState(null);

  const [auditData, setAuditData] = useState([]);
  const [auditDate, setAuditDate] = useState(getToday());
  const [masterFilter, setMasterFilter] = useState('All');
  const [showAuditDetail, setShowAuditDetail] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);

  const timerRef = useRef(null);
  const rawLoginLogsRef = useRef([]);

  function getToday() {
    const now = new Date();
    const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    return ist.toISOString().substring(0, 10);
  }

  function generateDateOptions() {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 90; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      options.push(d.toISOString().substring(0, 10));
    }
    return options;
  }

  const dateOptions = generateDateOptions();

  useEffect(() => {
    const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
    const role = userData.roleCode || userData.role || '';
    setUserRole(role);

    if (role === 'SYSADMIN') {
      setActiveTab('errorLogs');
      loadErrorLogs(getToday());
    }

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // ========== ERROR LOGS ==========
  const loadErrorLogs = async (date) => {
    setLoading(true);
    try {
      const res = await getErrorLogs(date || errorDate);
      setErrorLogs(res.data || []);
    } catch { toast.error('Failed to load error logs'); }
    finally { setLoading(false); }
  };

  const errorHeaders = [
    { displayName: 'Timestamp', columnName: 'timestamp', dataType: 'text', width: 180, displayIndication: true },
    { displayName: 'Source', columnName: 'source', dataType: 'text', width: 80, displayIndication: true },
    { displayName: 'File', columnName: 'fileName', dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Line', columnName: 'lineNumber', dataType: 'text', width: 50, displayIndication: true },
    { displayName: 'Message', columnName: 'message', dataType: 'text', width: 400, displayIndication: true },
    { displayName: 'Event', columnName: 'event', dataType: 'text', width: 150, displayIndication: true },
    { displayName: 'User', columnName: 'userId', dataType: 'text', width: 180, displayIndication: true },
  ];

  // ========== LOGIN LOGS ==========
  const loadLoginLogs = async (date) => {
    setLoading(true);
    try {
      const res = await getLoginLogs(date || loginDate);
      rawLoginLogsRef.current = res.data || [];
      const processed = processLoginLogs(rawLoginLogsRef.current);
      setLoginLogs(processed);
      // Start timer for active sessions
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const updated = processLoginLogs(rawLoginLogsRef.current);
        setLoginLogs(updated);
      }, 1000);
    } catch { toast.error('Failed to load login logs'); }
    finally { setLoading(false); }
  };

  const processLoginLogs = (logs) => {
    const loginEvents = logs.filter(l => l.action === 'LOGIN' || l.action === 'SESSION_TAKEOVER');
    const logoutEvents = logs.filter(l => l.action === 'LOGOUT' || l.action === 'SESSION_KICKED' || l.action === 'SESSION_TIMEOUT');
    const failedEvents = logs.filter(l => l.action === 'LOGIN_FAILED');

    // Sort logins by time descending - latest first
    const sortedLogins = [...loginEvents].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));

    const result = [];

    // Track which user already has their latest login marked
    const latestLoginPerUser = new Set();

    for (const login of sortedLogins) {
      const loginTime = parseISTTimestamp(login.timestamp);

      // Find matching logout for this specific session
      const matchingLogout = logoutEvents.find(lo =>
        lo.userId === login.userId && lo.sessionId === login.sessionId
      );

      let duration = '';
      let activeStatus = '';
      let logoutTimeDisplay = '';
      let logoutReason = '';

      if (matchingLogout) {
        // Has explicit logout
        const logoutTime = parseISTTimestamp(matchingLogout.timestamp);
        duration = formatDuration(logoutTime - loginTime);
        activeStatus = 'Logged Out';
        logoutTimeDisplay = matchingLogout.timestamp;
        logoutReason = matchingLogout.reason || '';
      } else if (!latestLoginPerUser.has(login.userId)) {
        // Latest login for this user with no logout = Active (live timer)
        // Check if superseded by a newer login from same user
        const newerLogin = sortedLogins.find(nl =>
          nl.userId === login.userId &&
          nl.timestamp > login.timestamp &&
          nl.sessionId !== login.sessionId
        );
        if (newerLogin) {
          const nextTime = parseISTTimestamp(newerLogin.timestamp);
          duration = formatDuration(nextTime - loginTime);
          activeStatus = 'Superseded';
          logoutReason = 'takeover';
          logoutTimeDisplay = newerLogin.timestamp;
        } else {
          const now = new Date();
          duration = formatDuration(now.getTime() - loginTime);
          activeStatus = 'Active';
        }
        latestLoginPerUser.add(login.userId);
      } else {
        // Not the latest login for this user, no logout = superseded or expired
        const nextLogin = sortedLogins.find(nl =>
          nl.userId === login.userId &&
          nl.timestamp > login.timestamp &&
          nl.sessionId !== login.sessionId
        );
        if (nextLogin) {
          const nextTime = parseISTTimestamp(nextLogin.timestamp);
          duration = formatDuration(nextTime - loginTime);
          activeStatus = 'Superseded';
          logoutReason = 'takeover';
          logoutTimeDisplay = nextLogin.timestamp;
        } else {
          const now = new Date();
          duration = formatDuration(now.getTime() - loginTime);
          activeStatus = 'Expired';
          logoutReason = 'session_expired';
        }
      }

      result.push({
        ...login,
        ip: formatIP(login.ip),
        _activeStatus: activeStatus,
        _duration: duration,
        _logoutTime: logoutTimeDisplay,
        _logoutReason: logoutReason
      });
    }

    // Add failed login attempts
    for (const failed of failedEvents) {
      result.push({
        ...failed,
        ip: formatIP(failed.ip),
        _activeStatus: 'Failed',
        _duration: '',
        _logoutTime: '',
        _logoutReason: ''
      });
    }

    result.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    return result;
  };

  const parseISTTimestamp = (ts) => {
    if (!ts) return 0;
    // Timestamp is in IST: "YYYY-MM-DD HH:MM:SS"
    // Parse as local time (user machine is IST)
    return new Date(ts.replace(' ', 'T')).getTime();
  };

  const formatDuration = (ms) => {
    if (!ms || ms < 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
  };

  const formatIP = (ip) => {
    if (!ip) return '';
    // Strip ::ffff: prefix from IPv4-mapped IPv6
    if (ip.startsWith('::ffff:')) return ip.substring(7);
    return ip;
  };

  const loginHeaders = [
    { displayName: 'Login Time', columnName: 'timestamp', dataType: 'text', width: 170, displayIndication: true },
    { displayName: 'User ID', columnName: 'userId', dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Action', columnName: 'action', dataType: 'text', width: 130, displayIndication: true, status: true },
    { displayName: 'Status', columnName: '_activeStatus', dataType: 'text', width: 100, displayIndication: true, status: true },
    { displayName: 'Duration', columnName: '_duration', dataType: 'text', width: 100, displayIndication: true },
    { displayName: 'Logout Time', columnName: '_logoutTime', dataType: 'text', width: 170, displayIndication: true },
    { displayName: 'Reason', columnName: '_logoutReason', dataType: 'text', width: 120, displayIndication: true },
    { displayName: 'IP', columnName: 'ip', dataType: 'text', width: 120, displayIndication: true },
  ];

  // ========== AUDIT TRAIL ==========
  const loadAuditTrail = async (date, master) => {
    setLoading(true);
    try {
      const res = await getAuditTrail(date || auditDate, master || masterFilter);
      setAuditData(res.data || []);
    } catch { toast.error('Failed to load audit trail'); }
    finally { setLoading(false); }
  };

  const renderMultiline = (value) => {
    if (!value) return '';
    const lines = String(value).split('\n');
    if (lines.length === 1) return value;
    return (<div>{lines.map((line, i) => <div key={i}>{line}</div>)}</div>);
  };

  const auditHeaders = [
    { displayName: 'Action', columnName: 'action', dataType: 'text', width: 90, displayIndication: true, status: true },
    { displayName: 'Master', columnName: 'master', dataType: 'text', width: 130, displayIndication: true },
    { displayName: 'Primary Value', columnName: 'primaryValue', dataType: 'text', width: 180, displayIndication: true },
    { displayName: 'Field', columnName: 'fieldName', dataType: 'text', width: 150, displayIndication: true, formatter: renderMultiline },
    { displayName: 'Original Value', columnName: 'originalValue', dataType: 'text', width: 200, displayIndication: true, formatter: renderMultiline },
    { displayName: 'Changed Value', columnName: 'changedValue', dataType: 'text', width: 200, displayIndication: true, formatter: renderMultiline },
    { displayName: 'Modified By', columnName: 'modifiedBy', dataType: 'text', width: 180, displayIndication: true },
    { displayName: 'Modified On', columnName: 'modifiedDate', dataType: 'text', width: 170, displayIndication: true },
  ];

  const masterOptions = ['All', 'User Master', 'Role Master', 'Branch Master', 'Dealer Master', 'Retailer Master', 'RSO Master', 'Brand Master'];

  const handleTabSelect = (key) => {
    setActiveTab(key);
    if (key === 'errorLogs' && errorLogs.length === 0) loadErrorLogs(errorDate);
    if (key === 'loginLogs' && loginLogs.length === 0) loadLoginLogs(loginDate);
    if (key === 'auditTrail' && auditData.length === 0) loadAuditTrail(auditDate, masterFilter);
    // Stop timer when leaving login logs
    if (key !== 'loginLogs' && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}

      <Container bsPrefix="main-body">
        <div className="page-title">Logging</div>
        <div className="page-sub-title">Error Logs, Login Logs & Audit Trail</div>

        <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3 custom-tabs">
          {userRole === 'SYSADMIN' && (
            <Tab eventKey="errorLogs" title="Error Logs">
              <ResponsiveTable
                headers={errorHeaders}
                data={errorLogs}
                dataType="ErrorLogs"
                noCheckbox={true}
                pageSize={20}
                onRowClick={item => { setSelectedError(item); setShowErrorDetail(true); }}
                customFilters={
                  <>
                    <Col md={2}>
                      <SearchSelect label="Date" name="errorDate" data={dateOptions} value={errorDate} onChange={e => setErrorDate(e.target.value)} />
                    </Col>
                    <Col style={{ width: '0.5cm', minWidth: '0.5cm', maxWidth: '0.5cm' }} />
                    <Col md="auto">
                      <Button className="add-btn" onClick={() => loadErrorLogs(errorDate)} style={{ height: '43px', marginTop: '0px' }}>Get</Button>
                    </Col>
                  </>
                }
              />
            </Tab>
          )}

          {userRole === 'SYSADMIN' && (
            <Tab eventKey="loginLogs" title="Login Log">
              <ResponsiveTable
                headers={loginHeaders}
                data={loginLogs}
                dataType="LoginLogs"
                noCheckbox={true}
                pageSize={20}
                onRowClick={item => { setSelectedLogin(item); setShowLoginDetail(true); }}
                customFilters={
                  <>
                    <Col md={2}>
                      <SearchSelect label="Date" name="loginDate" data={dateOptions} value={loginDate} onChange={e => setLoginDate(e.target.value)} />
                    </Col>
                    <Col style={{ width: '0.5cm', minWidth: '0.5cm', maxWidth: '0.5cm' }} />
                    <Col md="auto">
                      <Button className="add-btn" onClick={() => loadLoginLogs(loginDate)} style={{ height: '43px', marginTop: '0px' }}>Get</Button>
                    </Col>
                  </>
                }
              />
            </Tab>
          )}

          {userRole === 'SYSADMIN' && (
            <Tab eventKey="auditTrail" title="Audit Trail">
              <style>{`
                #AuditTrail_table tbody td {
                  vertical-align: top !important;
                }
              `}</style>
              <ResponsiveTable
                headers={auditHeaders}
                data={auditData}
                dataType="AuditTrail"
                noCheckbox={true}
                pageSize={20}
                onRowClick={item => { setSelectedAudit(item); setShowAuditDetail(true); }}
                customFilters={
                  <>
                    <Col md={2}>
                      <SearchSelect label="Date" name="auditDate" data={dateOptions} value={auditDate} onChange={e => setAuditDate(e.target.value)} />
                    </Col>
                    <Col md={2}>
                      <SearchSelect label="Master" name="masterFilter" data={masterOptions} value={masterFilter} onChange={e => setMasterFilter(e.target.value)} />
                    </Col>
                    <Col style={{ width: '0.5cm', minWidth: '0.5cm', maxWidth: '0.5cm' }} />
                    <Col md="auto">
                      <Button className="add-btn" onClick={() => loadAuditTrail(auditDate, masterFilter)} style={{ height: '43px', marginTop: '0px' }}>Get</Button>
                    </Col>
                  </>
                }
              />
            </Tab>
          )}
        </Tabs>
      </Container>

      {/* Error Detail Modal */}
      <Modal show={showErrorDetail} onHide={() => setShowErrorDetail(false)} size="lg" backdrop="static">
        <Modal.Header closeButton><Modal.Title>Error Detail</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedError && (
            <>
              <Row><Col md={6}><InputBox label="Timestamp" value={selectedError.timestamp || ''} readOnly /></Col><Col md={6}><InputBox label="Source" value={selectedError.source || ''} readOnly /></Col></Row>
              <Row><Col md={8}><InputBox label="File" value={selectedError.fileName || ''} readOnly /></Col><Col md={4}><InputBox label="Line" value={selectedError.lineNumber || ''} readOnly /></Col></Row>
              <Row><Col md={12}><InputBox label="Event" value={selectedError.event || ''} readOnly /></Col></Row>
              <Row><Col md={12}><InputBox label="User" value={selectedError.userId || ''} readOnly /></Col></Row>
              <Row><Col md={12}><InputBox label="Message" value={selectedError.message || ''} readOnly /></Col></Row>
              <Row><Col md={12}><Form.Group className="mb-3"><Form.Label>Stack Trace</Form.Label><Form.Control as="textarea" rows={8} value={selectedError.stack || 'No stack trace'} readOnly style={{ fontFamily: 'monospace', fontSize: '12px' }} /></Form.Group></Col></Row>
              {selectedError.additionalData && <Row><Col md={12}><Form.Group className="mb-3"><Form.Label>Additional Data</Form.Label><Form.Control as="textarea" rows={3} value={selectedError.additionalData || ''} readOnly style={{ fontFamily: 'monospace', fontSize: '12px' }} /></Form.Group></Col></Row>}
              {selectedError.url && <Row><Col md={12}><InputBox label="URL" value={selectedError.url || ''} readOnly /></Col></Row>}
              {selectedError.userAgent && <Row><Col md={12}><InputBox label="User Agent" value={selectedError.userAgent || ''} readOnly /></Col></Row>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowErrorDetail(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* Login Detail Modal */}
      <Modal show={showLoginDetail} onHide={() => setShowLoginDetail(false)} size="lg" backdrop="static">
        <Modal.Header closeButton><Modal.Title>Login Detail</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedLogin && (
            <>
              <Row><Col md={6}><InputBox label="Login Time" value={selectedLogin.timestamp || ''} readOnly /></Col><Col md={6}><InputBox label="User ID" value={selectedLogin.userId || ''} readOnly /></Col></Row>
              <Row><Col md={6}><InputBox label="Action" value={selectedLogin.action || ''} readOnly /></Col><Col md={6}><InputBox label="Status" value={selectedLogin._activeStatus || ''} readOnly /></Col></Row>
              <Row><Col md={6}><InputBox label="Duration" value={selectedLogin._duration || ''} readOnly /></Col><Col md={6}><InputBox label="Logout Time" value={selectedLogin._logoutTime || (selectedLogin._activeStatus === 'Active' ? 'Still active' : '')} readOnly /></Col></Row>
              <Row><Col md={6}><InputBox label="Reason" value={selectedLogin._logoutReason || ''} readOnly /></Col><Col md={6}><InputBox label="IP Address" value={selectedLogin.ip || ''} readOnly /></Col></Row>
              <Row><Col md={6}><InputBox label="Session ID" value={selectedLogin.sessionId || ''} readOnly /></Col></Row>
              {selectedLogin.attemptsCount > 0 && <Row><Col md={6}><InputBox label="Attempts Remaining" value={String(selectedLogin.attemptsCount)} readOnly /></Col></Row>}
              {selectedLogin.userAgent && <Row><Col md={12}><InputBox label="User Agent" value={selectedLogin.userAgent || ''} readOnly /></Col></Row>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowLoginDetail(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* Audit Detail Modal */}
      <Modal show={showAuditDetail} onHide={() => setShowAuditDetail(false)} size="lg" backdrop="static">
        <Modal.Header closeButton><Modal.Title>Audit Record Detail</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedAudit && (
            <>
              <Row><Col md={4}><InputBox label="Action" value={selectedAudit.action || ''} readOnly /></Col><Col md={4}><InputBox label="Master" value={selectedAudit.master || ''} readOnly /></Col><Col md={4}><InputBox label="Primary Value" value={selectedAudit.primaryValue || ''} readOnly /></Col></Row>
              <Row><Col md={6}><InputBox label="Field Changed" value={selectedAudit.fieldName || ''} readOnly /></Col><Col md={6}><InputBox label="Modified On" value={selectedAudit.modifiedDate || ''} readOnly /></Col></Row>
              <Row><Col md={6}><InputBox label="Original Value" value={selectedAudit.originalValue || ''} readOnly /></Col><Col md={6}><InputBox label="Changed Value" value={selectedAudit.changedValue || ''} readOnly /></Col></Row>
              <Row><Col md={12}><InputBox label="Modified By" value={selectedAudit.modifiedBy || ''} readOnly /></Col></Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowAuditDetail(false)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export default AuditTrail;
